import CreatableSelect from 'react-select/creatable';
import { withAsyncPaginate } from 'react-select-async-paginate';
import { ReactElement, useEffect, useState } from 'react';
import type { CreatableProps } from 'react-select/creatable';
import type { UseAsyncPaginateParams, ComponentProps } from 'react-select-async-paginate';
import type { GroupBase } from 'react-select';
import themes from 'styles/themes';
import { IndicatorsContainer, MyOption } from './customize_select';
import { makeStyles } from '@material-ui/core';
import './style.scss';

export interface OptionType {
    id?: string;
    value: string;
    title?: string;
    label: string;
    subject?: string;
    name?: string;
    descriptions?: string[];
}

type AsyncPaginateCreatableProps<
    OptionType,
    Group extends GroupBase<OptionType>,
    Additional,
    IsMulti extends boolean,
> = CreatableProps<OptionType, IsMulti, Group> &
    UseAsyncPaginateParams<OptionType, Group, Additional> &
    ComponentProps<OptionType, Group, IsMulti>;

type AsyncPaginateCreatableType = <
    OptionType,
    Group extends GroupBase<OptionType>,
    Additional,
    IsMulti extends boolean = false,
>(
    props: AsyncPaginateCreatableProps<OptionType, Group, Additional, IsMulti>,
) => ReactElement;

interface IProps {
    field: string;
    isLoading: boolean;
    isMulti?: boolean;
    loadOptions: Function;
    error: boolean;
    isDisabled: boolean;
    placeholder: string;
    defaultValue: OptionType[];
    handleSelectItem: Function;
    onChangeInput: Function;
}

const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect) as AsyncPaginateCreatableType;

const useContainerStyle = makeStyles(() => ({
    container: {
        position: 'relative',
        marginTop: '30px !important',
        '& .css-1hwfws3': {
            paddingLeft: 0,
        },
    },
    selectIcon: {
        position: 'absolute',
        right: '10px',
        top: '50%',
    },
    headerLabel: {
        fontSize: '14px',
        position: 'absolute',
        fontFamily: 'PoppinsRegular',
        marginBottom: 10,
        color: themes.palette.common.white,
        transform: 'scale(0.9) translateX(-2px)',
        fontWeight: 600,
        lineHeight: '0px',
    },
}));
const AsyncCustomizeSelect = ({
    field,
    isLoading,
    loadOptions,
    error,
    isDisabled,
    placeholder,
    defaultValue,
    handleSelectItem,
    onChangeInput,
    isMulti = true,
}: IProps) => {
    const classes = useContainerStyle();
    const [value, setValue] = useState('');
    const [showHeader, setShowHeader] = useState(false);
    const [listDefault, setListDefault] = useState(defaultValue);
    useEffect(() => {
        if ((listDefault && listDefault.length > 0) || value) {
            setShowHeader(true);
        }
    }, [listDefault, value]);

    useEffect(() => {
        if (defaultValue && defaultValue.length > 0) {
            setListDefault(defaultValue);
        } else {
            setListDefault([]);
        }
    }, [defaultValue]);

    const handleSelect = (option) => {
        handleSelectItem(option);
        setListDefault(option);
    };
    const handleInputChange = (newValue) => {
        onChangeInput && newValue && onChangeInput(field, newValue);
        setValue(newValue);
        return newValue;
    };

    return (
        <div className={`customize-select-wrap ${classes.container}`}>
            {placeholder && (
                <p
                    className={classes.headerLabel}
                    style={{
                        color: error ? 'red' : themes.palette.primary.main,
                        display: showHeader ? 'block' : 'none',
                    }}
                >
                    {placeholder}
                </p>
            )}
            <CreatableAsyncPaginate
                inputValue={value}
                isLoading={isLoading}
                value={defaultValue}
                loadOptions={loadOptions}
                onChange={(e) => handleSelect(e)}
                isMulti={isMulti}
                components={{ IndicatorsContainer, Option: MyOption }}
                isDisabled={isDisabled}
                onInputChange={handleInputChange}
                getValue={handleInputChange}
                placeholder={!showHeader ? (placeholder ? placeholder : 'Select...') : null}
                styles={{
                    placeholder: (base) => ({
                        color: error ? 'red' : '#1a1a1a80',
                        fontFamily: 'PoppinsRegular',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '14px',
                    }),
                    control: (base) => ({
                        padding: '10px 0 0 0',
                        margin: '10px 0 0 0',
                        borderBottom: `1px solid ${themes.palette.grey['300']}`,
                    }),
                    multiValue: (styles, { data }) => {
                        return {
                            ...styles,
                            backgroundColor: themes.palette.primary.main,
                            fontFamily: 'PoppinsRegular',
                            fontSize: '14px',
                        };
                    },
                    multiValueLabel: (styles, { data }) => ({
                        ...styles,
                        color: themes.palette.common.white,
                        fontFamily: 'PoppinsRegular',
                        fontSize: '14px',
                    }),
                }}
                onFocus={() => {
                    setShowHeader(true);
                }}
                onBlur={() => {
                    if (listDefault.length === 0) {
                        setShowHeader(false);
                    }
                }}
            />
        </div>
    );
};

export default AsyncCustomizeSelect;
